import { z } from "zod";
import {
  approveAndMergePrContract,
  cardDescriptionDesc,
  createSubtaskContract,
  deleteSubtaskContract,
  listSubtasksContract,
  updateSubtaskContract,
} from "@project/shared/tool-contracts";
import { defineTool } from "../harness/index.js";
import { defineContractTool } from "./contract-tool.js";
import type { AgentConnection } from "../connection/agent-connection.js";
import { textResult } from "./helpers.js";

export function buildUpdateTaskTool(connection: AgentConnection) {
  return defineTool(
    "update_task_plan",
    "Save the plan and/or description to the current task. In auto/building mode, save the plan BEFORE writing code and keep it current as the approach evolves — post it, then build; never pause the build waiting for approval. For children use update_subtask; for title/tags/PR use update_task_properties.",
    {
      plan: z.string().optional().describe("The task plan in markdown"),
      description: z
        .string()
        .optional()
        .describe(cardDescriptionDesc("Updated task description")),
    },
    async ({ plan, description }) => {
      try {
        await connection.call("updateTaskFields", {
          sessionId: connection.sessionId,
          plan,
          description,
        });
        return textResult("Task updated successfully.");
      } catch (error) {
        // Surface the real rejection (e.g. the description cap) — a generic
        // "Failed to update task." leaves the agent with nothing to act on.
        return textResult(
          `Failed to update task: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    },
  );
}

export function buildHandoffTool(connection: AgentConnection) {
  return defineTool(
    "handoff_to_agent",
    "Hand this task off to an implementer agent for the build phase — mid-conversation, same session, no restart. Call this once the plan is compiled and saved (update_task_plan). The server swaps this task to the difficulty-sized implementer agent (which may run at a different model level), announces the handoff in the activity log + chat, and switches you into build mode to start implementing. Size the work with the storyPoints arg (or set it first via update_task_properties). Returns the implementer's name + model.",
    {
      storyPoints: z
        .number()
        .int()
        .positive()
        .optional()
        .describe(
          "Difficulty sizing (1=Common, 2=Magic, 3=Rare, 5=Unique, 8=Pack) — picks which implementer agent takes over. Omit to use the task's current story points.",
        ),
      message: z
        .string()
        .optional()
        .describe("Optional kickoff note posted to the chat alongside the handoff notice."),
    },
    async ({ storyPoints, message }) => {
      try {
        const result = await connection.handoffToImplementer({
          ...(storyPoints !== undefined && { storyPoints }),
          ...(message !== undefined && { message }),
        });
        if (!result.handedOff) {
          return textResult(`Handoff did not complete: ${result.reason ?? "unknown reason"}`);
        }
        return textResult(
          `Handed off to ${result.agentName} (${result.model}). Now in build mode — start implementing the plan.`,
        );
      } catch (error) {
        return textResult(
          `Failed to hand off: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    },
  );
}

function buildCreateSubtaskTool(connection: AgentConnection) {
  return defineContractTool(
    createSubtaskContract,
    async ({
      title,
      description,
      plan,
      ordinal,
      storyPointValue,
      followParentStatus,
      dependsOn,
    }) => {
      try {
        const result = await connection.call("createSubtask", {
          sessionId: connection.sessionId,
          title,
          ...(description !== undefined && { description }),
          ...(plan !== undefined && { plan }),
          ...(storyPointValue !== undefined && { storyPointValue }),
          ...(ordinal !== undefined && { ordinal }),
          ...(followParentStatus !== undefined && { followParentStatus }),
          ...(dependsOn !== undefined && { dependsOn }),
        });
        return textResult(`Subtask created with ID: ${result.id} (slug: ${result.slug})`);
      } catch (error) {
        return textResult(
          `Failed to create subtask: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    },
  );
}

function buildUpdateSubtaskTool(connection: AgentConnection) {
  return defineContractTool(
    updateSubtaskContract,
    async ({
      subtaskId,
      title,
      description,
      plan,
      status,
      agentIdOrName,
      storyPointValue,
      followParentStatus,
      dependsOn,
    }) => {
      try {
        await connection.call("updateSubtask", {
          sessionId: connection.sessionId,
          subtaskId,
          ...(title !== undefined && { title }),
          ...(description !== undefined && { description }),
          ...(plan !== undefined && { plan }),
          ...(status !== undefined && { status }),
          ...(agentIdOrName !== undefined && { agentIdOrName }),
          ...(storyPointValue !== undefined && { storyPointValue }),
          ...(followParentStatus !== undefined && { followParentStatus }),
          ...(dependsOn !== undefined && { dependsOn }),
        });
        return textResult("Subtask updated.");
      } catch (error) {
        return textResult(`Failed: ${error instanceof Error ? error.message : "Unknown error"}`);
      }
    },
  );
}

function buildDeleteSubtaskTool(connection: AgentConnection) {
  return defineContractTool(deleteSubtaskContract, async ({ subtaskId }) => {
    try {
      await connection.call("deleteSubtask", {
        sessionId: connection.sessionId,
        subtaskId,
      });
      return textResult("Subtask deleted.");
    } catch (error) {
      return textResult(`Failed: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  });
}

function buildListSubtasksTool(connection: AgentConnection) {
  return defineContractTool(
    listSubtasksContract,
    async ({ verbose }) => {
      try {
        const subtasks = await connection.call("listSubtasks", {
          sessionId: connection.sessionId,
          view: verbose ? "full" : "compact",
        });
        return textResult(JSON.stringify(subtasks, null, 2));
      } catch {
        return textResult("Failed to list subtasks.");
      }
    },
    { annotations: { readOnlyHint: true } },
  );
}

function buildPackTools(connection: AgentConnection) {
  return [
    defineTool(
      "start_child_cloud_build",
      "Start a cloud build (codespace) for a child task. Preconditions: child status is `Open`, story points set, and an agent assigned — satisfy all three with update_subtask (status/agentIdOrName/storyPointValue) first; none happen automatically. A PACK_CHILD_LIMIT error is backpressure, not failure: check list_subtasks packSlots for which children hold the in-flight slots, merge/stop one, then retry. On a feature-branch pack, dev is merged into the pack branch first so the child branches from a fresh base; a conflict is reported in the result and never blocks the launch.",
      {
        childTaskId: z.string().describe("The child task ID to start a cloud build for"),
      },
      async ({ childTaskId }) => {
        try {
          const result = await connection.call("startChildCloudBuild", {
            sessionId: connection.sessionId,
            childTaskId,
          });
          const started = `Cloud build started for child task: ${result.childTaskId}`;
          const sync = result.baseSync;
          if (sync?.error) {
            return textResult(
              `${started}\n\nBase sync: dev → ${sync.branch} failed — ${sync.error}. ` +
                `The child was launched from the un-synced pack branch. Merge dev into ${sync.branch} ` +
                `in your local checkout, resolve the conflicts, and push before firing more children.`,
            );
          }
          return textResult(started);
        } catch (error) {
          return textResult(
            `Failed to start child cloud build: ${error instanceof Error ? error.message : "Unknown error"}`,
          );
        }
      },
    ),
    defineTool(
      "stop_child_build",
      "Send a graceful stop signal to a running child build's agent. Not a force-kill — the agent may take a moment to wind down. Stopping a child eventually frees its PACK_CHILD_LIMIT build slot (see list_subtasks packSlots).",
      {
        childTaskId: z.string().describe("The child task ID whose build should be stopped"),
      },
      async ({ childTaskId }) => {
        try {
          await connection.call("stopChildBuild", {
            sessionId: connection.sessionId,
            childTaskId,
          });
          return textResult(`Stop signal sent to child task: ${childTaskId}`);
        } catch (error) {
          return textResult(
            `Failed to stop child build: ${error instanceof Error ? error.message : "Unknown error"}`,
          );
        }
      },
    ),
    defineContractTool(approveAndMergePrContract, async ({ childTaskId }) => {
      try {
        const result = await connection.call("approveAndMergePR", {
          sessionId: connection.sessionId,
          childTaskId,
        });
        if (result.merged) {
          return textResult(
            `PR #${result.prNumber} approved and merged for task ${result.childTaskId}. Task status updated to ReviewDev.`,
          );
        }
        return textResult(
          `PR #${result.prNumber} merge queued for task ${result.childTaskId} — CI checks still in progress. The PR will auto-merge when all checks pass. Do NOT proceed as if merged. Wait for the child task status to change to ReviewDev before continuing.`,
        );
      } catch (error) {
        return textResult(
          `Failed to approve and merge PR: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    }),
  ];
}

export function buildPmTools(
  connection: AgentConnection,
  options?: { includePackTools?: boolean },
) {
  const tools = [
    buildUpdateTaskTool(connection),
    buildCreateSubtaskTool(connection),
    buildUpdateSubtaskTool(connection),
    buildDeleteSubtaskTool(connection),
    buildListSubtasksTool(connection),
  ];
  if (!options?.includePackTools) return tools;
  return [...tools, ...buildPackTools(connection)];
}
